import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, CalendarClock, Check, ListChecks, AlertCircle } from "lucide-react";
import crm, { apiErr } from "./crmApi";
import { useCrm } from "./CrmApp";
import { PageHeader, Tabs, Modal, Field, StatusBadge, PriorityBadge, EmptyState, SkeletonRows, activeOpts, fmtDT, isOverdue } from "./ui";

const VIEWS = [["my", "My Tasks"], ["due_today", "Due Today"], ["overdue", "Overdue"], ["upcoming", "Upcoming"], ["completed", "Completed"], ["team", "Team Tasks"]];

export function Tasks() {
  const [view, setView] = useState("my");
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [sched, setSched] = useState(null);
  const load = () => { setErr(""); crm.tasks(view).then((d) => setItems(d.items)).catch((e) => setErr(apiErr(e))); };
  useEffect(() => { setItems(null); load(); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const complete = (t) => crm.completeTask(t.id).then(() => { toast.success("Task completed"); load(); }).catch((e) => toast.error(apiErr(e)));
  return (
    <div className="crm-fade">
      <PageHeader title="Follow-ups & Tasks" count={items?.length} actions={<button onClick={() => setShowNew(true)} data-testid="add-task-btn" className="crm-btn crm-btn-primary"><Plus size={15} /> New Task</button>} />
      <Tabs items={VIEWS} value={view} onChange={setView} prefix="task-view" />
      <div className="crm-card overflow-hidden">
        {err ? <EmptyState icon={AlertCircle} title="Couldn't load tasks" text={err} action={<button onClick={load} className="crm-btn crm-btn-secondary">Retry</button>} />
          : !items ? <SkeletonRows rows={5} cols={4} />
          : items.length === 0 ? <EmptyState icon={ListChecks} title="Nothing here" text="No tasks in this view." />
          : <ul className="divide-y divide-[#E4EBE4]">{items.map((t) => {
            const od = t.status === "Pending" && isOverdue(t.due_date);
            return (
              <li key={t.id} className={`flex items-center gap-3 px-4 py-3 flex-wrap sm:flex-nowrap ${od ? "bg-[#FEF2F2]/50" : ""}`} data-testid={`task-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[14px] truncate">{t.title}</p>
                  <p className="text-[12px] text-[#5F6E64] mt-0.5">{t.task_type} · <span className={od ? "text-[#B91C1C] font-semibold" : ""}>{od && "Overdue · "}due {fmtDT(t.due_date)}</span>{view === "team" && t.assigned_to_name && <> · {t.assigned_to_name}</>}{t.lead_id && <> · <Link to={`/crm/leads/${t.lead_id}`} className="text-[#236B45] font-medium hover:underline">Open lead</Link></>}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <PriorityBadge p={t.priority} /><StatusBadge s={t.status} />
                  {t.status !== "Completed" && <>
                    <button onClick={() => setSched(t)} data-testid={`task-resched-${t.id}`} className="crm-btn crm-btn-secondary crm-btn-sm"><CalendarClock size={13} /> Reschedule</button>
                    <button onClick={() => complete(t)} data-testid={`task-complete-${t.id}`} className="crm-btn crm-btn-primary crm-btn-sm"><Check size={13} /> Complete</button>
                  </>}
                </div>
              </li>
            );
          })}</ul>}
      </div>
      {showNew && <NewTask onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
      {sched && <RescheduleModal task={sched} onClose={() => setSched(null)} onDone={() => { setSched(null); load(); }} />}
    </div>
  );
}

function NewTask({ onClose, onDone }) {
  const { user } = useCrm();
  const [emps, setEmps] = useState([]);
  const [settings, setSettings] = useState({});
  const [f, setF] = useState({ title: "", task_type: "Call", due_date: "", priority: "Medium", assigned_to: "", notes: "" });
  useEffect(() => { crm.employees().then(setEmps).catch(() => {}); crm.settings().then(setSettings).catch(() => {}); }, []);
  const canAssign = ["owner", "sales_manager"].includes(user.role);
  const types = activeOpts(settings, "task_types", f.task_type);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.due_date) return toast.error("Pick a due date");
    try { await crm.createTask({ ...f, due_date: new Date(f.due_date).toISOString() }); toast.success("Task created"); onDone(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <Modal title="New Task" onClose={onClose} testid="new-task-modal">
      <form onSubmit={submit} className="space-y-3.5">
        <Field label="Title" required><input required data-testid="task-title" className="crm-in" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type"><select className="crm-in" data-testid="task-type" value={f.task_type} onChange={(e) => setF({ ...f, task_type: e.target.value })}>{(types.length ? types : ["Call"]).map((x) => <option key={x}>{x}</option>)}</select></Field>
          <Field label="Priority"><select className="crm-in" data-testid="task-priority" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>{["High", "Medium", "Low"].map((x) => <option key={x}>{x}</option>)}</select></Field>
        </div>
        <Field label="Due date & time" required><input type="datetime-local" className="crm-in" data-testid="task-due" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></Field>
        {canAssign && <Field label="Assign to"><select className="crm-in" data-testid="task-assignee" value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })}><option value="">Me</option>{emps.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></Field>}
        <Field label="Notes"><textarea className="crm-in" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="crm-btn crm-btn-secondary">Cancel</button><button data-testid="task-save" className="crm-btn crm-btn-primary">Create Task</button></div>
      </form>
    </Modal>
  );
}

function RescheduleModal({ task, onClose, onDone }) {
  const [dt, setDt] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!dt) return toast.error("Pick a new date");
    try { await crm.rescheduleTask(task.id, new Date(dt).toISOString()); toast.success("Rescheduled"); onDone(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <Modal title="Reschedule task" onClose={onClose} width="max-w-sm" testid="resched-modal">
      <form onSubmit={submit} className="space-y-3.5">
        <p className="text-[13px] text-[#43544A]"><b>{task.title}</b><br /><span className="text-[#5F6E64]">Currently due {fmtDT(task.due_date)}</span></p>
        <Field label="New date & time" required><input type="datetime-local" className="crm-in" data-testid="resched-date" value={dt} onChange={(e) => setDt(e.target.value)} /></Field>
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="crm-btn crm-btn-secondary">Cancel</button><button data-testid="resched-save" className="crm-btn crm-btn-primary">Save new date</button></div>
      </form>
    </Modal>
  );
}
